/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { DragSource, DropTarget } from 'react-dnd';
import { flow, isEqual, cloneDeep } from 'lodash';
import Tooltip from 'react-tooltip-lite';
import Store from '../../store/store';
import DropDownMenu from '../Common/dropDownMenu';
import Holon from '../Buttons/holon';
import { resultCardId } from '../../utils/string';
import ItemTypes from '../../constants/itemTypes';
import modalTypes from '../../constants/modalTypes';
import { openModal } from '../../actions/modalActions';
import { updateWorkPackage } from '../../actions/workPackageActions';
import { startDrag, endDrag } from '../../actions/notificationActions';
import { allows } from '../../utils/authorization';
import SourceTypes from '../../constants/sourceTypes';
import UserInitialsButton from '../Common/userInitialsButton';
import { isKanbanMode } from '../../selectors/teamSelector';
import { filterWorkersByAuthority } from '../../selectors/userSelector';

const resultSource = {
  beginDrag(props) {
    Store.dispatch(startDrag());
    return {
      result: props.result,
      index: props.index,
      source: props.source
    };
  },
  canDrag(props) {
    return props.childCard ? false : allows('update', props.result);
  },
  endDrag(props, monitor) {
    const switchedResult = monitor.getDropResult();
    const sourceOutsideSprint = [
      SourceTypes.GETTING_READY,
      SourceTypes.READY
    ].includes(props.source);

    if (
      switchedResult &&
      switchedResult.result &&
      monitor.didDrop() &&
      !sourceOutsideSprint
    ) {
      props.removeCard(switchedResult.index, switchedResult.result);
    }
    if (!monitor.didDrop()) props.resetResults();

    Store.dispatch(endDrag());
  }
};

const resultTarget = {
  hover(props, monitor, component) {
    const dragIndex = monitor.getItem().index;
    const hoverIndex = props.index;
    const hoveredCard = props.result;

    if (props.source === monitor.getItem().source) {
      if (dragIndex === hoverIndex) {
        return;
      }

      props.moveCardHover(dragIndex, hoveredCard);
      monitor.getItem().index = hoverIndex; // eslint-disable-line no-param-reassign
    }
  },

  drop(props, monitor) {
    const draggedItem = monitor.getItem();
    // const multiProducts = Store.getState().defaultOptionsReducer
    //   .multipleProductsSelected;
    if (props.source === draggedItem.source) {
      props.moveCardDrop(draggedItem);
    }
  }
};

const collectSource = (connectSource, monitor) => ({
  connectDragSource: connectSource.dragSource(),
  isDragging: monitor.isDragging()
});

const collectTarget = (connectDropTarget) => ({
  connectDropTarget: connectDropTarget.dropTarget()
});

class Result extends React.Component {
  // If more than one product is selected, we can disable DnD/ranking
  multipleProductsSelected = Store.getState().defaultOptionsReducer
    .multipleProductsSelected;

  constructor(props) {
    super(props);
    this.state = {
      dropDown: {},
      isTitleEditable: false,
      result: props.result,
      showTooltip: false,
      placeHolderName: props.result.short_name
    };
  }

  componentWillReceiveProps(newProps) {
    const { owner_id: newID } = newProps.result;
    const { owner_id: oldID } = this.state.result;

    if (newID !== oldID) {
      this.setState({ result: newProps.result });
    }
  }

  componentDidUpdate(prevProps) {
    const { result } = prevProps;
    if (!isEqual(result, this.props.result)) {
      const clonedResult = cloneDeep(this.props.result);
      this.setState({
        result: clonedResult,
        placeHolderName: clonedResult.short_name
      });
    }
  }

  openParent = (e) => {
    let posx = 0;
    let posy = 0;
    if (e.pageX || e.pageY) {
      posx = e.pageX;
      posy = e.pageY;
    } else if (e.clientX || e.clientY) {
      posx =
        e.clientX +
        document.body.scrollLeft +
        document.documentElement.scrollLeft;
      posy =
        e.clientY +
        document.body.scrollTop +
        document.documentElement.scrollTop;
    }
    const { result } = this.state;

    if (result.parent_id) {
      this.props.addWrapParent(result.parent_id);
      Store.dispatch(
        openModal(
          modalTypes.wrapCard,
          { item: result, type: 'story', posx, posy },
          'wrapCard'
        )
      );
    }
  };

  openModal = (e) => {
    e.preventDefault();

    const { result } = this.state;

    if (result.id && result.id > 0) {
      Store.dispatch(
        openModal(modalTypes.resultDetail, {
          id: result.id,
          result: result.temporaryId ? result : undefined
        })
      );
    }
  };

  toggleDropDownOwner = (e) => {
    e.stopPropagation();
    const { users } = this.props;
    let { dropDown } = this.state;
    const { result } = this.state;

    if (dropDown.id) {
      dropDown = {};
    } else {
      const { target: element } = e;
      const classes = 'planwall-drop-menu';
      dropDown = {
        result,
        classes,
        users,
        elementClicked: element,
        elementAttached: element,
        parent:
          element.closest('#scrollable-wall-container') ||
          element.closest('.card-wall'),
        type: 'changeOwner',
        id: result.id,
        closeDropdown: () => this.setState({ dropDown: {} })
      };
    }
    this.setState({ dropDown });
  };

  makeTitleEditable = () => this.setState({ isTitleEditable: true });

  // handles real time updates of the text area character by character
  handleOnChange = ({ target }) => {
    this.setState({ placeHolderName: target.value });
  };

  handleOnBlur = (e) => {
    // if new name is '' then interrupt name change
    const oldName = this.state.result.short_name;
    const newName = e.target.value;
    if (newName == '') {
      // if user attempts to rename to empty string, reset to old name
      e.target.value = oldName;
      this.setState({ isTitleEditable: false });
      this.setState({ placeHolderName: oldName });
    } else {
      const { result } = this.state;
      // check for value for new name otherwise update the name
      if (e.target.value) {
        if (result.id)
          // undefined if a new child card
          this.props.updateWorkPackage(result.id, {
            work_package: { short_name: e.target.value }
          });
        else
          this.props.updateTemporaryStory(result.temporaryId, {
            short_name: newName
          });
        this.setState({ isTitleEditable: false });
      }
    }
  };

  openTooltip = () => {
    const { showTooltip } = this.state;
    if (!showTooltip) {
      this.setState({ showTooltip: true });
    }
  };

  closeTooltip = () => {
    const { showTooltip } = this.state;
    if (showTooltip) {
      this.setState({ showTooltip: false });
    }
  };

  showCard = () => {
    const { isDragging, noHolon, users } = this.props;
    const { dropDown, isTitleEditable, result } = this.state;
    const allowEdit = allows('update', result);
    const { defaultOptions } = Store.getState().defaultOptionsReducer;
    const isDefect = result.result_type && result.result_type.kind === 'Defect';
    let valueDefaults =
      defaultOptions.value_defaults && defaultOptions.value_defaults.values;
    if (isDefect) {
      valueDefaults =
        defaultOptions.severity_defaults &&
        defaultOptions.severity_defaults.values;
    }
    const riskDefaults =
      defaultOptions.risk_defaults && defaultOptions.risk_defaults.values;
    const sizeDefaults =
      defaultOptions.size_defaults && defaultOptions.size_defaults.values;
    const vrs = result.result_type ? result.result_type.type_attributes : {};
    const dragClass = allowEdit ? 'move-cursor' : '';
    let vrsValue = '-';
    if (
      valueDefaults &&
      vrs.value &&
      valueDefaults[vrs.value] &&
      valueDefaults[vrs.value].length > 0
    ) {
      const selectedValue = valueDefaults[vrs.value];

      vrsValue = isDefect
        ? `S${selectedValue[selectedValue.length - 1]}`
        : selectedValue[0];
    }

    const deletedClassName = result.deleted_at ? 'deleted' : '';
    const blockedClassName = result.blocked ? 'blocked-card' : '';
    const draggingClassName =
      isDragging || result.isDragging ? 'dragging-opacity' : '';
    const borderStyle = { borderLeft: '1px solid #C2C2C4' };
    return (
      <div
        className={`panel panel-default wall-row ${deletedClassName} ${blockedClassName} ${draggingClassName}`}
      >
        <div className={`panel-heading ${dragClass}`}>
          <div className="panel-title">
            <div className="col-sm-2  col-xs-12">
              <a onClick={this.openModal}>
                {result.id ? resultCardId(result) : 'New'}
              </a>
            </div>
            <div className="col-sm-7 col-xs-12 text-center">
              {result.parent_id && !noHolon && (
                <Holon action={this.openParent} />
              )}
            </div>
            <div className="col-sm-1 col-sm-offset-0 col-xs-2 col-xs-offset-3 text-center value">
              {vrsValue}
            </div>
            <div
              className="col-sm-1 col-xs-2 text-center risk"
              style={borderStyle}
            >
              {riskDefaults && vrs.risk ? riskDefaults[vrs.risk][0] : '-'}
            </div>
            <div
              className="col-sm-1 col-xs-2 text-center points"
              style={borderStyle}
            >
              {sizeDefaults && vrs.size ? sizeDefaults[vrs.size] : '-'}
            </div>
          </div>
        </div>
        <div
          onClick={allowEdit ? this.makeTitleEditable : null}
          className="panel-body"
        >
          {isTitleEditable ? (
            <textarea
              autoFocus={isTitleEditable}
              value={this.state.placeHolderName}
              onBlur={this.handleOnBlur}
              onChange={this.handleOnChange}
            />
          ) : (
            <p>{this.state.placeHolderName || result.name}</p>
          )}
        </div>
        <div className="panel-footer">
          <UserInitialsButton
            className="right"
            onClick={this.toggleDropDownOwner}
            isDisabled={!allowEdit || !users || users.length === 0}
            userId={result.owner_id}
            users={users}
          />
        </div>
        {dropDown && dropDown.id && (
          <DropDownMenu
            type={dropDown.type}
            dropDown={dropDown}
            key={dropDown.id}
          />
        )}
      </div>
    );
  };

  showMiniCard = () => {
    const { isDragging, source } = this.props;
    const { result, showTooltip } = this.state;
    const deletedClassName = result.deleted_at ? 'deleted' : '';
    const blockedClassName = result.blocked ? 'blocked-card' : '';
    const miniCardBorderClassName = `mini-card-${source.replace(
      'Results',
      ''
    )}`;
    const draggingClassName = isDragging ? 'dragging-opacity' : '';

    // const cursorClass = this.multipleProductsSelected ? 'move-cursor' : '';

    const item = (
      <>
        <span className="link" onClick={this.openModal}>
          {result.id ? resultCardId(result) : 'New'}
        </span>
        <div className="glyphicon glyphicon-move move-glyph" />
      </>
    );

    return (
      <div
        className={`mini-result-card ${deletedClassName} ${blockedClassName} ${miniCardBorderClassName} ${draggingClassName}`}
        onMouseEnter={this.openTooltip}
        onMouseLeave={this.closeTooltip}
      >
        <Tooltip
          isOpen={showTooltip}
          content={result.name}
          useDefaultStyles
          className="mini-result-card-content"
        >
          {item}
        </Tooltip>
      </div>
    );
  };

  render() {
    const { connectDragSource, connectDropTarget, showTrash } = this.props;
    const { result } = this.state;

    if (!showTrash && result.deleted_at) return null;

    return connectDropTarget(
      connectDragSource(this.props.mini ? this.showMiniCard() : this.showCard())
    );
  }
}

Result.propTypes = {
  result: PropTypes.object.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  updateTemporaryStory: PropTypes.func.isRequired,
  users: PropTypes.array,
  mini: PropTypes.bool,
  isDragging: PropTypes.bool,
  isNewFeature: PropTypes.bool,
  noHolon: PropTypes.bool,
  connectDropTarget: PropTypes.func,
  childCard: PropTypes.bool
};

const mapStateToProps = (state, ownProps) => {
  const { result } = ownProps;
  const users = filterWorkersByAuthority(state, result) || [];

  return {
    showTrash: state.trashReducer.showTrash,
    isKanban: isKanbanMode(state),
    users
  };
};
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateWorkPackage
    },
    dispatch
  );
export default flow([
  DragSource(ItemTypes.RESULT, resultSource, collectSource),
  DropTarget(ItemTypes.RESULT, resultTarget, collectTarget)
])(connect(mapStateToProps, mapDispatchToProps)(Result));
